/**
 * Inline-mount render decisions (#592) — pure, so the fail-closed gap and
 * the expand state machine are pinnable by tests (the scroll-dock model).
 *
 * The blessed div (`div.bhai-<customerId>-chat`) hosts the widget in-flow.
 * Mode resolution (tenant setting vs data-bhai-inline attribute) lives in
 * @bluehat/shared/widget-design/inline; this module decides what the
 * inline mount PAINTS for a resolved-or-pending mode.
 */
import type { InlineMode } from "@bluehat/shared/widget-design/inline";

export type InlineRenderPhase = "blank" | "collapsed" | "chat";

/**
 * What the inline mount renders.
 *  - mode null (config not landed, no div attribute): fail-closed blank —
 *    mirrors the #589 rule that nothing paints before the tenant's config.
 *  - "open": the chat, always.
 *  - "expand": the collapsed skinned surface until `open`, then the chat.
 *    `barAllowed` carries the per-URL scope verdict + the design's own
 *    bar-enabled flag — a scope deny must hide the collapsed surface here
 *    exactly as it hides the floating bar.
 */
export function inlineRenderPhase(
  mode: InlineMode | null,
  open: boolean,
  barAllowed: boolean,
  /** True once the first paint would be FINAL: config landed (the div
   *  attribute reveals the mode early but never the look — skin=null before
   *  config means UNKNOWN) AND the page-voice payload resolved (the capsule
   *  row differs with vs without section data; painting mid-flight flipped
   *  the layout one frame later — the 2026-08-17 bench flicker). Theme is
   *  never part of the wait: skin classes derive from the skin alone, so
   *  even the 2s lost-payload failsafe paints dressed. */
  surfaceReady: boolean = true,
): InlineRenderPhase {
  if (mode == null) return "blank";
  if (mode === "open" || open) return "chat";
  if (!surfaceReady) return "blank";
  return barAllowed ? "collapsed" : "blank";
}

/** Hug tolerance: a container whose height tracks the collapsed surface
 *  this closely is auto-sized (it has no explicit height — it is hugging
 *  its content). Small container padding still reads as hugging. A fixed
 *  min-height threshold was tried first and failed: the collapsed skinned
 *  surface itself is ~286px tall (stage cards + capsule), so any absolute
 *  cutoff misreads a tall bar as a designer-sized panel (#592 bench). */
export const INLINE_HUG_TOLERANCE_PX = 24;

/**
 * Height the widget applies to its host on expand when the blessed div gave
 * it no room — `null` means the container owns the size (the existing
 * inline contract). Measured pre-expand: container hugging the collapsed
 * host ⇒ auto-sized ⇒ supply a working FIXED height (fixed, not min- —
 * an auto chain would grow per message, the documented composer-off-screen
 * bug). A designer-sized container is never overridden; designers adjust
 * via CSS on `[data-bhai-inline-state="expanded"]` (page CSS beats shadow
 * :host on the host element). Cleared on collapse.
 */
export function inlineExpandedHostHeight(containerPx: number, hostPx: number): string | null {
  return Math.abs(containerPx - hostPx) < INLINE_HUG_TOLERANCE_PX ? "min(560px, 70vh)" : null;
}

/**
 * How long ONE agent chip stays before the next slides in.
 *
 * Scaled to reading time, because a rotation that gives "Pricing?" and "Do you
 * handle H2S locations on a 24-hour callout?" the same beat either flashes the
 * long one past unread or leaves the short one sitting there.
 *
 * ~200 wpm is the common quiet-reading figure; at ~5.5 characters a word that
 * is ~18 chars/second, or ~55ms per character. The fixed part is not reading
 * time — it is the beat before reading starts, when the eye has to notice the
 * chip changed at all.
 *
 * Clamped at both ends: below the floor the rotation reads as a flicker, and
 * above the ceiling a visitor waiting on the next suggestion thinks it stopped.
 */
export const AGENT_CHIP_MIN_MS = 2400;
export const AGENT_CHIP_MAX_MS = 7000;

export function agentChipDwellMs(question: string): number {
  const chars = question.trim().length;
  const ms = 1500 + chars * 55;
  return Math.min(AGENT_CHIP_MAX_MS, Math.max(AGENT_CHIP_MIN_MS, ms));
}
